import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CourierRates, SiteFacts, SiteLanguage, SitePageView } from '../interfaces/site-page';

/// What the server render carries for a page that has no published row: the browser shows the not-found page at once
/// instead of asking again.
interface Missing { missing: true; }

const FACTS = makeStateKey<SiteFacts>('site-facts');
const COURIER_RATES = makeStateKey<CourierRates>('courier-rates');

/// The site's own pages and fragments, and the facts they may state (SitePagesController). Every answer the server
/// render gets travels in the page (TransferState), so a page drawn on the server is never asked for again by the
/// browser and nothing on it changes when the browser takes over.
@Injectable({
    providedIn: 'root'
})
export class SitePagesService {
    private readonly base = environment.apiUrl + '/api/SitePages';

    /// Payment methods, support line and plans: one request per visit, shared by every page and the organisation node.
    readonly facts$: Observable<SiteFacts | null>;

    constructor(private http: HttpClient, private state: TransferState, @Inject(PLATFORM_ID) private platformId: any) {
        const carried = state.get<SiteFacts>(FACTS, null);

        this.facts$ = (carried
            ? of(carried)
            : http.get<SiteFacts>(this.base + '/Facts').pipe(
                tap(facts => {
                    if (isPlatformServer(platformId) && facts) {
                        state.set(FACTS, facts);
                    }
                }),
                catchError(() => of(null))))
            .pipe(shareReplay(1));
    }

    /// The couriers' own published figures that are still fresh, for the courier charges tool; carried from the server render.
    courierRates(): Observable<CourierRates | null> {
        const carried = this.state.get<CourierRates>(COURIER_RATES, null);
        if (carried) {
            return of(carried);
        }
        return this.http.get<CourierRates>(this.base + '/CourierRates').pipe(
            tap(rates => {
                if (isPlatformServer(this.platformId) && rates) {
                    this.state.set(COURIER_RATES, rates);
                }
            }),
            catchError(() => of(null)));
    }

    /// A seller lead (the Dhaka Hub waitlist, an Enterprise enquiry) - SellerLeadsController.
    sendLead(form: { [field: string]: string }): Observable<{ result: string, errors?: { [field: string]: string }, wait?: number }> {
        return this.http.post<{ result: string, errors?: { [field: string]: string }, wait?: number }>(environment.apiUrl + '/api/SellerLeads/Send', form);
    }

    /// The contact form's signed token, which the lead forms carry too.
    formToken(): Observable<string | null> {
        return this.http.get<{ token?: string, Token?: string }>(environment.apiUrl + '/api/Contact/Form').pipe(
            map(answer => (answer && (answer.token || answer.Token)) || null),
            catchError(() => of(null)));
    }

    /// One page in one language. Fails with the HttpErrorResponse when there is none - a 404 when the key has no
    /// published row in that language - so the caller can show the not-found page.
    page(key: string, lang: SiteLanguage): Observable<SitePageView> {
        const stateKey = makeStateKey<SitePageView | Missing>('site-page:' + lang + ':' + key);
        const carried = this.state.get<SitePageView | Missing>(stateKey, null);
        if (carried) {
            return (carried as Missing).missing
                ? throwError(new HttpErrorResponse({ status: 404, statusText: 'Not Found' }))
                : of(carried as SitePageView);
        }

        return this.http.get<SitePageView>(this.base + '/Page', { params: { key, lang } }).pipe(
            tap(page => {
                if (isPlatformServer(this.platformId) && page) {
                    this.state.set(stateKey, page);
                }
            }),
            catchError((error: HttpErrorResponse) => {
                if (isPlatformServer(this.platformId) && error && error.status === 404) {
                    this.state.set(stateKey, { missing: true } as Missing);
                }
                return throwError(error);
            }));
    }

    /// Fragments by key ("home.hero"), each in the language asked for or in English where it has no Bangla row (its
    /// `language` says which). A key with no published row is left out. Never fails: a failed request gives none, and
    /// the blocks they fill keep their own words.
    fragments(keys: string[], lang: SiteLanguage): Observable<{ [key: string]: SitePageView }> {
        const list = keys.slice().sort().join(',');
        const stateKey = makeStateKey<{ [key: string]: SitePageView }>('site-fragments:' + lang + ':' + list);
        const carried = this.state.get<{ [key: string]: SitePageView }>(stateKey, null);
        if (carried) {
            return of(carried);
        }

        return this.http.get<{ [key: string]: SitePageView }>(this.base + '/Fragments', { params: { keys: list, lang } }).pipe(
            tap(fragments => {
                if (isPlatformServer(this.platformId)) {
                    this.state.set(stateKey, fragments || {});
                }
            }),
            catchError(() => of({})));
    }
}
